import { useRef, useState } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';
import { MdEmail } from 'react-icons/md';
import { Link } from 'react-router-dom';

import {
  Alert,
  Button,
  Typography,
  InputAdornment,
  TextField,
  CircularProgress,
} from '@mui/material';
import { initiateResetPassword } from 'api/api';
import ConfirmationModal from 'components/ConfirmationModal';
import ErrorMessage from 'components/ErrorMessage';
import { RecoverPasswordFormProps, RecoverPasswordRequestDTO } from 'dtos/RecoverPasswordDTO';
import { useFormik } from 'formik';
import { recoverPasswordFormSchema } from 'shared/validators';
import {
  Container,
  AppHeader,
  AppLogo,
  CardLogin,
  LoginLogo,
  LoginCardHeader,
  LoginCardBody,
  LoginCardFooter,
  NeedsValidation,
} from 'styles/Styles';
import { handleApiError } from 'utils/handleApiError';
import { getBackgroundImage, getLogo, getSlugFromURL } from 'utils/whiteLabel';

import { version } from '../../constants';

interface RecoverPasswordProps {
  headerColor?: string;
  btnColor?: string;
}

export default function RecoverPassword(props: RecoverPasswordProps) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const reRef = useRef<ReCAPTCHA>();
  const PROJECT = getSlugFromURL();

  const initialValues: RecoverPasswordFormProps = {
    email: '',
  };

  const { values, errors, isSubmitting, handleChange, handleSubmit, setSubmitting } = useFormik({
    validationSchema: recoverPasswordFormSchema,
    initialValues,
    onSubmit: (values: RecoverPasswordFormProps) => {
      handleSubmitRecoverPassword(values);
    },
    validateOnChange: false,
    validateOnBlur: false,
  });

  const handleSubmitRecoverPassword = async (fields: RecoverPasswordFormProps) => {
    try {
      const reToken = await reRef.current?.executeAsync();
      reRef?.current?.reset();

      if (!reToken) return;

      const payload: RecoverPasswordRequestDTO = {
        email: fields.email,
        reToken,
      };

      await initiateResetPassword(payload);

      setIsModalOpen(true);
    } catch (error) {
      handleApiError(error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleConfirmModal = () => {
    setIsModalOpen(false);
  };

  return (
    <>
      <Container style={{ backgroundImage: `url(${getBackgroundImage(PROJECT)})` }}>
        <AppHeader>
          <CardLogin>
            <LoginLogo>
              <AppLogo src={getLogo(PROJECT)} height="135" alt="logo" />
            </LoginLogo>
            <LoginCardHeader style={{ backgroundColor: props.headerColor ?? '#0071bc' }}>
              Recuperação de Senha
            </LoginCardHeader>
            <LoginCardBody>
              <Alert className="mb1" severity="info">
                Preencha o campo abaixo com seu email e você receberá um token de confirmação para
                alterar sua senha
              </Alert>

              <NeedsValidation onSubmit={handleSubmit}>
                <div>
                  <TextField
                    id="input-email"
                    type="email"
                    fullWidth
                    required
                    value={values?.email}
                    error={!!errors?.email}
                    onChange={handleChange('email')}
                    label="Email"
                    placeholder="joao@gmail.com"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <MdEmail />
                        </InputAdornment>
                      ),
                    }}
                  />
                  {errors?.email && <ErrorMessage error={errors?.email} />}
                </div>

                <ReCAPTCHA
                  sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY as string}
                  size="invisible"
                  ref={reRef as React.RefObject<ReCAPTCHA>}
                />

                <div className="gap-2 d-grid">
                  <Button
                    disabled={isSubmitting}
                    color="primary"
                    type="submit"
                    variant="contained"
                    size="large"
                  >
                    {!isSubmitting ? 'ENVIAR' : <CircularProgress color="inherit" size={24} />}
                  </Button>
                </div>
              </NeedsValidation>
            </LoginCardBody>
            <LoginCardFooter>
              <div className="d-flex justify-content-center">
                <Link to="/login">Fazer login</Link>
              </div>
              <div className="mt-3 d-flex justify-content-center">
                <Typography variant="caption" color="textSecondary">
                  {`versão: ${version}`}
                </Typography>
              </div>
            </LoginCardFooter>
          </CardLogin>
        </AppHeader>
      </Container>

      <ConfirmationModal
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onConfirm={handleConfirmModal}
      >
        <div>
          Favor conferir a caixa de entrada no email <b>{values.email}</b> com o link para redefinir
          sua senha.
        </div>
      </ConfirmationModal>
    </>
  );
}
